/* @flow */

import * as React from 'react';

import classnames from 'classnames';
import generateUniqueId from 'lodash/uniqueId';

import './checkbox.module.css';

export type Props = {
    // TODO: Figure out how to make this work.  Might need a newer flow than 0.53
    // ...HTMLInputElement,
    label?: React.Node,
    checked?: boolean,
    /** Should a larger box be shown (true), or the browser-default style (false)? */
    isLarge?: boolean,
    /** Should the checkbox be top aligned (aligned with the first line of text) */
    isTopAligned?: boolean,
    /** This should only be used to do things like stopPropagation */
    onClick?: (SyntheticInputEvent<*>) => void,
    onChange?: (boolean, SyntheticInputEvent<*>) => any,
    /** Sometimes we need to get the underlying node ref. */
    checkboxRef?: (?HTMLElement) => void,
    /** When there's an error (for example in validation), set this to true to get a red border */
    hasError?: boolean,
    helpComponent?: React.Node,
    customCheckboxStyle?: Object,
    disabled?: ?boolean,
    /** If true, the checkbox will show an X regardless of props.checked and props.disabled */
    showX?: boolean,
    labelColor?: 'light' | 'dark',
};

/**
 * The `isLarge` variant of checkboxes should be used for new UI development.
 *
 * Note: all valid props of an `input` element are also valid for this component.
 */
export const Checkbox = (props: Props) => {
    const {
        label = '',
        checkboxRef = () => {},
        onChange,
        checked = false,
        customCheckboxStyle,
        helpComponent,
        isLarge,
        hasError,
        isTopAligned,
        showX,
        labelColor = 'dark',
        ...restProps
    } = props;
    const [uniqueId] = React.useState(generateUniqueId('checkbox_'));

    const handleChange = React.useCallback(
        (e: SyntheticInputEvent<*>) => {
            if (typeof onChange === 'function') {
                onChange(e.target.checked, e);
            }
        },
        [onChange]
    );

    const handleKeyDown = React.useCallback(
        (e: SyntheticKeyboardEvent<*>) => {
            if (e.key === 'Enter') {
                const newChecked = !checked;
                if (typeof onChange === 'function') {
                    const inputEvent = {
                        ...e,
                        target: {
                            ...e.target,
                            checked: newChecked,
                        },
                        currentTarget: e.currentTarget,
                        type: 'change',
                        nativeEvent: e.nativeEvent,
                    };
                    // $FlowIgnore Mocking our SyntheticInputEvent for the onChange callback
                    onChange(newChecked, inputEvent);
                }
            }
        },
        [checked, onChange]
    );

    const containerStyle = classnames({
        'is-large': isLarge,
        container: !isLarge,
        'has-error': hasError,
        'is-top-aligned': isTopAligned,
        'no-label': !label,
        'is-x-state': showX,
        'light-label': labelColor === 'light',
    });

    return (
        <div styleName={containerStyle}>
            <input
                {...restProps}
                type='checkbox'
                id={uniqueId}
                styleName='checkbox'
                onChange={handleChange}
                style={customCheckboxStyle}
                onKeyDown={handleKeyDown}
                checked={checked}
                disabled={props.disabled}
                ref={checkboxRef}
            />
            <label htmlFor={uniqueId} onClick={props.onClick}>
                {label}
            </label>
            {helpComponent || undefined}
        </div>
    );
};

Checkbox.displayName = 'Checkbox';
